import { logger } from '@commander/logger';
import { analytics } from '@commander/product-analytics';
import { toast } from '@commander/ui';
import { createContext, type ReactNode, useContext, useRef, useState } from 'react';
import { SMSComposer } from '../components/sms/SMSComposer';
import { useCreateSMSThread } from '../hooks/mutations/smsMutations';
import type { SMSTemplate } from '../types';

// SMS Data interface - data sent from the composer
interface SMSData {
  phoneNumber?: string;
  contactName?: string;
  contactId?: string;
  message: string;
  segments: number;
  estimatedCost: number;
  templateId?: string;
  variables?: Record<string, string>;
}

// API payload interface for creating SMS threads
interface CreateThreadPayload {
  body: string;
  tenant_contact_id?: string;
  phone_number?: string;
  contact_name?: string;
}

// Options interfaces for different compose scenarios
interface ComposeSMSOptions {
  phoneNumber?: string;
  contactName?: string;
  contactId?: string;
  onSuccess?: (response: any) => void;
}

interface ComposeToContactOptions {
  contactId: string;
  name?: string;
  phoneNumber?: string;
  onSuccess?: (response: any) => void;
}

// Context value interface
interface SMSComposerContextValue {
  compose: (options?: ComposeSMSOptions) => void;
  composeToContact: (contact: ComposeToContactOptions) => void;
  composeWithTemplate: (template: SMSTemplate, options?: ComposeSMSOptions) => void;
  close: () => void;
}

const SMSComposerContext = createContext<SMSComposerContextValue | null>(null);

export function useSMSComposer() {
  const context = useContext(SMSComposerContext);
  if (!context) {
    throw new Error('useSMSComposer must be used within SMSComposerProvider');
  }
  return context;
}

interface SMSComposerProviderProps {
  children: ReactNode;
}

export function SMSComposerProvider({ children }: SMSComposerProviderProps) {
  // Modal state
  const [isOpen, setIsOpen] = useState(false);

  // Recipient data
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [contactName, setContactName] = useState<string>('');
  const [contactId, setContactId] = useState<string | undefined>(undefined);

  // Template
  const [template, setTemplate] = useState<SMSTemplate | undefined>(undefined);

  // Success callback
  const onSuccessCallbackRef = useRef<((response: any) => void) | undefined>(undefined);

  // Mutations
  const createSMSThreadMutation = useCreateSMSThread();

  const resetState = () => {
    setPhoneNumber('');
    setContactName('');
    setContactId(undefined);
    setTemplate(undefined);
    onSuccessCallbackRef.current = undefined;
  };

  const close = () => {
    setIsOpen(false);
    resetState();
  };

  const compose = (options?: ComposeSMSOptions) => {
    resetState();

    if (options) {
      if (options.phoneNumber) setPhoneNumber(options.phoneNumber);
      if (options.contactName) setContactName(options.contactName);
      if (options.contactId) setContactId(options.contactId);
      if (options.onSuccess) onSuccessCallbackRef.current = options.onSuccess;
    }

    // Track SMS composed event
    analytics.track('sms_composed' as any, {
      recipient_count: options?.phoneNumber ? 1 : 0,
      related_contact: options?.contactId,
    });

    setIsOpen(true);
  };

  const composeToContact = (contact: ComposeToContactOptions) => {
    resetState();

    setContactId(contact.contactId);
    if (contact.name) setContactName(contact.name);
    if (contact.phoneNumber) setPhoneNumber(contact.phoneNumber);
    if (contact.onSuccess) onSuccessCallbackRef.current = contact.onSuccess;

    // Track SMS composed event
    analytics.track('sms_composed' as any, {
      recipient_count: 1,
      related_contact: contact.contactId,
    });

    setIsOpen(true);
  };

  const composeWithTemplate = (smsTemplate: SMSTemplate, options?: ComposeSMSOptions) => {
    resetState();

    setTemplate(smsTemplate);

    if (options) {
      if (options.phoneNumber) setPhoneNumber(options.phoneNumber);
      if (options.contactName) setContactName(options.contactName);
      if (options.contactId) setContactId(options.contactId);
      if (options.onSuccess) onSuccessCallbackRef.current = options.onSuccess;
    }

    // Track SMS template used event
    analytics.track('sms_template_used', {
      template_name: smsTemplate.name,
      template_category: smsTemplate.category,
    });

    // Also track SMS composed when using template
    analytics.track('sms_composed' as any, {
      template_used: smsTemplate.name,
      related_contact: options?.contactId,
    });

    setIsOpen(true);
  };

  const handleSend = async (smsData: SMSData) => {
    // Build the API payload
    const payload: CreateThreadPayload = { body: smsData.message };

    // Validation: need either contactId or phoneNumber
    if (smsData.contactId) {
      payload.tenant_contact_id = smsData.contactId;
    } else if (smsData.phoneNumber) {
      payload.phone_number = smsData.phoneNumber;
      if (smsData.contactName) {
        payload.contact_name = smsData.contactName;
      }
    } else {
      logger.error('SMS send attempted without contactId or phoneNumber');
      toast.error('Either contactId or phoneNumber is required');
      throw new Error('Either contactId or phoneNumber is required');
    }

    try {
      const response = await createSMSThreadMutation.mutateAsync(payload);
      toast.success('Message sent successfully');

      // Track SMS sent event
      analytics.track('sms_sent' as any, {
        recipient_count: 1,
        message_length: smsData.message.length,
        template_used: smsData.templateId,
        related_contact: smsData.contactId,
      });

      // Execute success callback if provided
      if (onSuccessCallbackRef.current) {
        onSuccessCallbackRef.current(response);
      }

      close();
    } catch (error: any) {
      logger.error('Failed to send SMS:', error);
      toast.error(error.message || 'Failed to send message');
      throw error; // Re-throw so component knows to keep modal open
    }
  };

  const contextValue: SMSComposerContextValue = {
    compose,
    composeToContact,
    composeWithTemplate,
    close,
  };

  return (
    <SMSComposerContext.Provider value={contextValue}>
      {children}
      <SMSComposer
        isOpen={isOpen}
        onClose={close}
        onSend={handleSend}
        template={template}
        contactId={contactId}
        contactName={contactName}
        phoneNumber={phoneNumber}
      />
    </SMSComposerContext.Provider>
  );
}
